import { renderMentionsAsText, type MessageDTO } from '@clanfire/shared';
import { useAuth } from '@/stores/auth';
import { isBlocked } from '@/stores/blocks';
import { memberName, useServers } from '@/stores/servers';
import { useConversations } from '@/stores/social';
import { useUsers } from '@/stores/users';
import { conversationTitle } from './conversations';
import { playSound } from './sounds';

/** Notification sound: a brighter one when you're mentioned. */
export function playChime(kind: 'message' | 'mention' = 'message') {
  playSound(kind);
}

export function desktopNotificationsSupported() {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export async function requestDesktopPermission(): Promise<NotificationPermission | 'unsupported'> {
  if (!desktopNotificationsSupported()) return 'unsupported';
  if (Notification.permission !== 'default') return Notification.permission;
  return Notification.requestPermission();
}

function previewText(message: MessageDTO): string {
  const users = useUsers.getState().users;
  const text = renderMentionsAsText(message.content, (id) => users[id]?.displayName);
  if (text.trim()) return text.length > 140 ? `${text.slice(0, 139)}…` : text;
  return message.attachments.length ? `Sent ${message.attachments.length === 1 ? 'an attachment' : `${message.attachments.length} attachments`}` : '';
}

/**
 * Decides whether an incoming message deserves a sound / desktop notification
 * based on user settings, the server's notification level and focus state.
 */
export function maybeNotify(message: MessageDTO, opts: { viewing: boolean; onClick: () => void }) {
  const { user, settings } = useAuth.getState();
  if (!user || !settings || message.author?.id === user.id || isBlocked(message.author?.id)) return;
  // Notes like "Alex added Sam to the group" don't need an alert.
  if (message.type !== 'DEFAULT') return;
  if (user.status === 'DND') return;
  const focused = document.visibilityState === 'visible' && document.hasFocus();
  if (opts.viewing && focused) return;

  const isDm = !!message.conversationId;
  const mentionsMe = message.mentions.includes(user.id) || (message.replyTo?.author?.id === user.id && message.replyPing) || message.mentionEveryone;
  let notify = false;
  let title = message.author?.displayName ?? 'New message';

  if (isDm) {
    notify = settings.notifyOnDirectMessages;
    const conversation = useConversations.getState().conversations[message.conversationId!];
    if (conversation?.type === 'GROUP') title = `${title} (${conversationTitle(conversation)})`;
  } else if (message.serverId) {
    const server = useServers.getState().servers[message.serverId];
    const channel = message.channelId ? useServers.getState().channels[message.channelId] : undefined;
    const level = server?.me.notificationLevel ?? 'ALL';
    // Server messages only alert on mentions/replies; everything else is an unread badge.
    notify = mentionsMe && settings.notifyOnMentions && level !== 'NOTHING';
    title = `${memberName(message.serverId, message.author?.id) ?? message.author?.displayName ?? 'Someone'} (#${channel?.name ?? 'channel'}, ${server?.name ?? 'server'})`;
  }
  if (!notify) return;

  if (settings.notificationSounds) playChime(mentionsMe ? 'mention' : 'message');
  if (settings.desktopNotifications && !focused && desktopNotificationsSupported() && Notification.permission === 'granted') {
    const n = new Notification(title, { body: previewText(message), icon: message.author?.avatarUrl ?? '/favicon.svg', tag: message.channelId ?? message.conversationId ?? undefined });
    n.onclick = () => {
      window.focus();
      opts.onClick();
      n.close();
    };
  }
}
